'use client';

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Sparkles, 
  FileText, 
  Copy,
  Pencil,
  Loader2,
  Check,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AskAIBar } from '@/components/ai';
import { ArtifactGeneratorInput, type AttachedFile } from '@/components/shared/artifact-generator-input';
import { useToast } from '@/hooks/use-toast';
import { useChat, useGenerateContent } from '@/hooks/use-ai';
import { useAuthToken } from '@/hooks/use-auth';
import type { Citation } from '@/lib/ai-client';
import { ProjectOverviewContent } from './project-overview-content';
import { VersionsDropdown, type Version } from '@/components/versions/versions-dropdown';

/**
 * Parse and format Project Overview JSON content to human-readable markdown
 */
function formatProjectOverviewContent(content: string): string {
  const trimmed = content.trim();

  // If content is HTML (saved from Lexical editor), return as-is
  if (trimmed.startsWith('<')) {
    return content;
  }

  // Check if content is already markdown
  if (trimmed.startsWith('#') || trimmed.startsWith('*') || trimmed.startsWith('-')) {
    return content;
  }

  // Try to parse as JSON
  try {
    const parsed = JSON.parse(content);
    
    // Check if this object contains actual content fields (not just schema definition)
    // These are the main content fields we expect from the template
    const hasContentFields = parsed.executive_summary || parsed.project_vision || 
                            parsed.key_objectives || parsed.target_audience ||
                            parsed.success_metrics || parsed.timeline;
    
    if (hasContentFields) {
      // Extract and format the content, ignoring schema definition fields
      return formatParsedOverview(parsed);
    }
    
    // If no content fields found, this might be just a schema definition
    if ((parsed.format === 'json' || parsed.json_schema || parsed.$schema) && !hasContentFields) {
      return '# Project Overview\n\nNo content has been generated yet. Click "Generate New" to create your project overview.';
    }

    // Format any other JSON structure
    return formatParsedOverview(parsed);
  } catch (e) {
    // JSON parsing failed - try to extract content using regex for common fields
    // This handles truncated JSON from the database
    console.warn('[ProjectOverview] JSON parse failed, attempting regex extraction:', e);
    
    const extractedContent = extractContentFromMalformedJson(content);
    if (extractedContent) {
      return extractedContent;
    }
    
    // If content looks like JSON but couldn't be parsed, show error
    if (trimmed.startsWith('{')) {
      return '# Project Overview\n\n*The stored content appears to be corrupted. Please click "Generate New" to create a fresh overview.*';
    }
    
    // Not valid JSON, return as-is (probably already markdown)
    return content;
  }
}

/**
 * Extract content from malformed/truncated JSON using regex
 */
function extractContentFromMalformedJson(content: string): string | null {
  const sections: string[] = [];
  
  // Try to extract executive_summary paragraphs
  const para1Match = content.match(/"paragraph_1"\s*:\s*"([^"]+)"/);
  const para2Match = content.match(/"paragraph_2"\s*:\s*"([^"]+)"/);
  if (para1Match || para2Match) {
    sections.push('## Executive Summary');
    sections.push('');
    if (para1Match) {
      sections.push(para1Match[1]);
      sections.push('');
    }
    if (para2Match) {
      sections.push(para2Match[1]);
      sections.push('');
    }
  }
  
  // Try to extract project_vision
  const visionMatch = content.match(/"project_vision"\s*:\s*"([^"]+)"/);
  if (visionMatch) {
    sections.push('');
    sections.push('## Project Vision');
    sections.push('');
    sections.push(visionMatch[1]);
    sections.push('');
  }
  
  // Try to extract key_objectives array
  const objectivesMatch = content.match(/"key_objectives"\s*:\s*\[([^\]]+)\]/);
  if (objectivesMatch) {
    sections.push('');
    sections.push('## Key Objectives');
    sections.push('');
    const objectives = objectivesMatch[1].match(/"([^"]+)"/g);
    if (objectives) {
      objectives.forEach((obj) => {
        const cleanObj = obj.replace(/"/g, '');
        sections.push(`- ${cleanObj}`);
      });
    }
    sections.push('');
  }
  
  // Try to extract target_audience
  const primaryRolesMatch = content.match(/"primary"\s*:\s*\{[^}]*"roles"\s*:\s*\[([^\]]+)\]/);
  const primaryDescMatch = content.match(/"primary"\s*:\s*\{[^}]*"description"\s*:\s*"([^"]+)"/);
  const secondaryRolesMatch = content.match(/"secondary"\s*:\s*\{[^}]*"roles"\s*:\s*\[([^\]]+)\]/);
  const secondaryDescMatch = content.match(/"secondary"\s*:\s*\{[^}]*"description"\s*:\s*"([^"]+)"/);
  
  if (primaryRolesMatch || secondaryRolesMatch) {
    sections.push('');
    sections.push('## Target Audience');
    sections.push('');
    
    if (primaryRolesMatch) {
      const roles = primaryRolesMatch[1].match(/"([^"]+)"/g)?.map(r => r.replace(/"/g, '')).join(', ');
      sections.push(`**Primary:** ${roles || ''}`);
      sections.push('');
      if (primaryDescMatch) sections.push(primaryDescMatch[1]);
      sections.push('');
    }
    
    if (secondaryRolesMatch) {
      const roles = secondaryRolesMatch[1].match(/"([^"]+)"/g)?.map(r => r.replace(/"/g, '')).join(', ');
      sections.push(`**Secondary:** ${roles || ''}`);
      sections.push('');
      if (secondaryDescMatch) sections.push(secondaryDescMatch[1]);
      sections.push('');
    }
  }
  
  if (sections.length > 0) {
    return sections.join('\n');
  }
  
  return null;
}

/**
 * Format a parsed Project Overview object into readable markdown
 */
function formatParsedOverview(data: Record<string, unknown>): string {
  const sections: string[] = [];

  // Executive Summary
  if (data.executive_summary) {
    const es = data.executive_summary as Record<string, string>;
    sections.push('## Executive Summary');
    sections.push('');
    if (es.paragraph_1) {
      sections.push(es.paragraph_1);
      sections.push('');
    }
    if (es.paragraph_2) {
      sections.push(es.paragraph_2);
      sections.push('');
    }
  }

  // Project Vision
  if (data.project_vision) {
    sections.push('');
    sections.push('## Project Vision');
    sections.push('');
    sections.push(data.project_vision as string);
    sections.push('');
  }

  // Key Objectives
  if (data.key_objectives && Array.isArray(data.key_objectives)) {
    sections.push('');
    sections.push('## Key Objectives');
    sections.push('');
    (data.key_objectives as string[]).forEach((obj) => {
      sections.push(`- ${obj}`);
    });
    sections.push('');
  }

  // Target Audience
  if (data.target_audience) {
    const ta = data.target_audience as Record<string, Record<string, unknown>>;
    sections.push('');
    sections.push('## Target Audience');
    sections.push('');
    
    if (ta.primary) {
      const roles = ta.primary.roles && Array.isArray(ta.primary.roles) 
        ? (ta.primary.roles as string[]).join(', ') 
        : '';
      sections.push(`**Primary:** ${roles}`);
      sections.push('');
      if (ta.primary.description) {
        sections.push(ta.primary.description as string);
        sections.push('');
      }
    }
    
    if (ta.secondary) {
      const roles = ta.secondary.roles && Array.isArray(ta.secondary.roles) 
        ? (ta.secondary.roles as string[]).join(', ') 
        : '';
      sections.push(`**Secondary:** ${roles}`);
      sections.push('');
      if (ta.secondary.description) {
        sections.push(ta.secondary.description as string);
        sections.push('');
      }
    }
  }

  // Success Metrics
  if (data.success_metrics && Array.isArray(data.success_metrics)) {
    sections.push('');
    sections.push('## Success Metrics');
    sections.push('');
    (data.success_metrics as Record<string, string>[]).forEach((metric, i) => {
      const name = metric.metric_name || `Metric ${i + 1}`;
      const value = metric.target_value || 'TBD';
      sections.push(`${i + 1}. **${name}:** ${value}`);
    });
    sections.push('');
  }

  // Scope
  if (data.scope) {
    const scope = data.scope as Record<string, string[]>;
    sections.push('');
    sections.push('## Scope');
    sections.push('');
    
    if (scope.in_scope && Array.isArray(scope.in_scope)) {
      sections.push('**In Scope:**');
      sections.push('');
      scope.in_scope.forEach(item => sections.push(`- ${item}`));
      sections.push('');
    }
    
    if (scope.out_of_scope && Array.isArray(scope.out_of_scope)) {
      sections.push('**Out of Scope:**');
      sections.push('');
      scope.out_of_scope.forEach(item => sections.push(`- ${item}`));
      sections.push('');
    }
  }

  // Timeline
  if (data.timeline && Array.isArray(data.timeline)) {
    sections.push('');
    sections.push('## Timeline');
    sections.push('');
    (data.timeline as Record<string, unknown>[]).forEach(phase => {
      const phaseName = phase.phase_name as string || 'Phase';
      const dateRange = phase.date_range as string || 'TBD';
      sections.push(`**${dateRange}** — ${phaseName}`);
      sections.push('');
      if (phase.focus && Array.isArray(phase.focus)) {
        (phase.focus as string[]).forEach(f => sections.push(`- ${f}`));
        sections.push('');
      }
    });
  }

  // Budget & Resources
  if (data.budget_and_resources) {
    const br = data.budget_and_resources as Record<string, unknown>;
    sections.push('');
    sections.push('## Budget & Resources');
    sections.push('');
    
    if (br.team_size) {
      // Format team size with role breakdown inline if available
      let teamInfo = `**Team Size:** ${br.team_size}`;
      if (br.role_breakdown && Array.isArray(br.role_breakdown)) {
        const rolesList = (br.role_breakdown as Record<string, unknown>[])
          .map(role => `${role.count} ${role.role}`)
          .join(', ');
        teamInfo += ` (${rolesList})`;
      }
      sections.push(teamInfo);
      sections.push('');
    }
    
    if (br.year_1_budget) {
      let budgetInfo = `**Budget:** ${br.year_1_budget}`;
      if (br.budget_notes) {
        budgetInfo += ` (${br.budget_notes})`;
      }
      sections.push(budgetInfo);
      sections.push('');
    }
  }

  // Risks & Assumptions
  if (data.risks_and_assumptions && Array.isArray(data.risks_and_assumptions)) {
    sections.push('');
    sections.push('## Risks & Assumptions');
    sections.push('');
    (data.risks_and_assumptions as string[]).forEach(item => {
      sections.push(`- ${item}`);
    });
    sections.push('');
  } else if (data.assumptions && Array.isArray(data.assumptions)) {
    sections.push('');
    sections.push('## Assumptions');
    sections.push('');
    (data.assumptions as string[]).forEach(item => {
      sections.push(`- ${item}`);
    });
    sections.push('');
  }

  // Evidence/Key Links
  if (data.evidence_refs && Array.isArray(data.evidence_refs)) {
    sections.push('');
    sections.push('## Key Links');
    sections.push('');
    (data.evidence_refs as Record<string, string>[]).forEach(ref => {
      const refType = ref.ref_type || 'link';
      const refId = ref.ref_id || '';
      const note = ref.note || '';
      sections.push(`- [${refType}] ${refId}${note ? ` — ${note}` : ''}`);
    });
    sections.push('');
  }

  // If no sections were added, return a placeholder
  if (sections.length === 0) {
    return '# Project Overview\n\nContent is being processed. Please try generating again.';
  }

  return sections.join('\n');
}

interface ProjectOverview {
  id: string;
  projectId: string;
  content: string;
  isDraft: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  template?: { id: string; name: string } | null;
  createdBy?: { id: string; name: string | null; email: string } | null;
}

interface ProjectOverviewPanelProps {
  projectId: string;
  projectName?: string;
  onCopy?: () => void;
  onContentGenerated?: (content: string) => void;
}

export function ProjectOverviewPanel({
  projectId,
  projectName = 'Enterprise Banking Platform',
  onCopy,
  onContentGenerated,
}: ProjectOverviewPanelProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [overview, setOverview] = useState<ProjectOverview | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editorKey, setEditorKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const initialEditorHtmlRef = useRef('');
  const isSyncingInitialEditorStateRef = useRef(false);
  const [showAskAI, setShowAskAI] = useState(false);
  const [versions, setVersions] = useState<Version[]>([]);
  const [isRestoringVersion, setIsRestoringVersion] = useState(false);
  // Viewing a specific version (without restoring)
  const [viewingVersion, setViewingVersion] = useState<Version | null>(null);
  const [viewingVersionContent, setViewingVersionContent] = useState<string | null>(null);
  const { toast } = useToast();
  const token = useAuthToken();
  
  // AI hooks
  const chatMutation = useChat(projectId);
  const generateMutation = useGenerateContent(projectId);
  
  // Format content for display (parse JSON to markdown if needed)
  // If viewing an old version, show that content instead
  const formattedContent = useMemo(() => {
    const contentToFormat = viewingVersionContent || overview?.content;
    if (!contentToFormat) return '';
    return formatProjectOverviewContent(contentToFormat);
  }, [overview?.content, viewingVersionContent]);

  // Fetch existing overview on mount
  useEffect(() => {
    const fetchOverview = async () => {
      if (!projectId || !token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/projects/${projectId}/overview`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        const data = await response.json();
        
        if (data.success && data.data) {
          setOverview(data.data);
        }
      } catch (error) {
        console.error('Error fetching overview:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOverview();
  }, [projectId, token]);

  // Fetch versions when overview is loaded
  const fetchVersions = useCallback(async () => {
    if (!projectId || !token || !overview) return;

    try {
      const response = await fetch(`/api/projects/${projectId}/overview/versions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      
      if (data.success && data.data) {
        // Transform to Version format
        const transformedVersions: Version[] = data.data.map((v: { 
          id: string; 
          version: number; 
          createdAt: string; 
          isCurrent?: boolean;
          createdBy?: { name: string | null };
        }) => ({
          id: v.id,
          name: `Version ${v.version}`,
          timestamp: new Date(v.createdAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          }),
          isCurrent: v.isCurrent || false,
        }));
        setVersions(transformedVersions);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  }, [projectId, token, overview]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  // Handle version selection (just view, don't restore)
  const handleVersionSelect = async (version: Version) => {
    // If selecting current version, clear viewing state
    if (version.isCurrent || version.id === 'current') {
      setViewingVersion(null);
      setViewingVersionContent(null);
      return;
    }
    
    if (!projectId || !token) return;

    // Find the version content from the versions list (the API returns content)
    // For now, fetch the version content from the server
    try {
      const response = await fetch(`/api/projects/${projectId}/overview/versions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      
      if (data.success && data.data) {
        const versionData = data.data.find((v: { id: string; content?: string }) => v.id === version.id);
        if (versionData && versionData.content) {
          setViewingVersion(version);
          setViewingVersionContent(versionData.content);
        }
      }
    } catch (error) {
      console.error('Error fetching version content:', error);
    }
  };

  // Handle restore - actually restore the version (creates new version with old content)
  const handleRestoreVersion = async () => {
    if (!viewingVersion || !projectId || !token) return;

    setIsRestoringVersion(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/overview/versions/${viewingVersion.id}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (data.success) {
        setOverview(data.data);
        setViewingVersion(null);
        setViewingVersionContent(null);
        await fetchVersions();
        toast({
          title: 'Version Restored',
          description: `Restored to ${viewingVersion.name}`,
        });
      } else {
        throw new Error(data.error?.message || 'Failed to restore version');
      }
    } catch (error) {
      toast({
        title: 'Restore Failed',
        description: error instanceof Error ? error.message : 'Failed to restore version',
        variant: 'destructive',
      });
    } finally {
      setIsRestoringVersion(false);
    }
  };

  // Cancel viewing old version
  const handleCancelViewingVersion = () => {
    setViewingVersion(null);
    setViewingVersionContent(null);
  };

  const handleGenerate = async (description: string, attachedFiles: AttachedFile[]) => {
    if (!projectId) {
      toast({
        title: 'Error',
        description: 'Project ID is required',
        variant: 'destructive',
      });
      return;
    }

    if (!token) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to generate content.',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);
    try {
      const fileContext = attachedFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size,
      }));

      const response = await fetch('/api/projects/generate-artifact', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          projectId,
          artifactType: 'overview',
          description,
          attachedFiles: fileContext,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Refetch the overview to get the saved version
        const overviewResponse = await fetch(`/api/projects/${projectId}/overview`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const overviewData = await overviewResponse.json();
        if (overviewData.success && overviewData.data) {
          setOverview(overviewData.data);
        }
        
        onContentGenerated?.(data.data.content);
        toast({
          title: 'Overview Generated',
          description: 'AI has generated your project overview.',
        });
      } else {
        throw new Error(data.message || 'Failed to generate overview');
      }
    } catch (error) {
      toast({
        title: 'Generation Failed',
        description: error instanceof Error ? error.message : 'Failed to generate overview',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!token || !projectId) return;

    setIsSaving(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/overview`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: editContent,
          isDraft: false,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setOverview(data.data);
        setIsEditing(false);
        initialEditorHtmlRef.current = '';
        isSyncingInitialEditorStateRef.current = false;
        // Refresh versions after save
        setTimeout(() => fetchVersions(), 500);
        toast({
          title: 'Saved',
          description: 'Overview updated successfully.',
        });
      } else {
        throw new Error(data.message || 'Failed to save');
      }
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save overview',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const normalizeEditorHtml = (content: string) => content.replace(/\s+/g, ' ').trim();

  const handleStartEdit = () => {
    // Initialize edit content with formatted content (converted from JSON if necessary)
    const initialContent = formattedContent || '';
    setEditContent(initialContent);
    // Capture baseline before any editor onChange event so first user edit is detected correctly.
    initialEditorHtmlRef.current = initialContent;
    // The editor normalizes markdown/JSON into HTML on mount; sync baseline to that normalized value once.
    isSyncingInitialEditorStateRef.current = true;
    setEditorKey((prev) => prev + 1);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent('');
    initialEditorHtmlRef.current = '';
    isSyncingInitialEditorStateRef.current = false;
  };

  const hasEditChanges =
    isEditing &&
    normalizeEditorHtml(editContent) !== normalizeEditorHtml(initialEditorHtmlRef.current);

  // Called by Lexical editor when content changes — useCallback to keep stable reference
  const handleInlineContentChange = useCallback((newContent: string) => {
    if (isSyncingInitialEditorStateRef.current) {
      initialEditorHtmlRef.current = newContent;
      isSyncingInitialEditorStateRef.current = false;
    }
    setEditContent(newContent);
  }, []);

  const handleAskAI = async (message: string): Promise<{ response: string; citations: Citation[] }> => {
    try {
      const result = await chatMutation.mutateAsync({ message });
      return result;
    } catch (error) {
      // Return error as response to be displayed in the UI
      return {
        response: error instanceof Error ? error.message : "Failed to get AI response. Please try again.",
        citations: [],
      };
    }
  };

  const escapeHtml = (unsafe: string) =>
    unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const textToHtmlBlocks = (text: string) =>
    text
      .trim()
      .split(/\n{2,}/)
      .filter(Boolean)
      .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br/>')}</p>`)
      .join('\n');

  const handleInsertAIResponse = (responseText: string) => {
    const currentBaseline = isEditing ? initialEditorHtmlRef.current : (formattedContent || '');
    const currentContent = isEditing ? editContent : (formattedContent || '');
    const aiHtml = textToHtmlBlocks(responseText);
    const nextContent = currentContent
      ? `${currentContent}\n${aiHtml}`
      : aiHtml;

    if (!isEditing) {
      initialEditorHtmlRef.current = currentBaseline;
      setIsEditing(true);
    }

    setEditContent(nextContent);
    // Force a remount so Lexical loads the newly inserted AI content.
    setEditorKey((prev) => prev + 1);
  };

  const handleCopy = () => {
    if (formattedContent) {
      navigator.clipboard.writeText(formattedContent);
      toast({
        title: "Copied",
        description: "Content copied to clipboard",
      });
    }
    onCopy?.();
  };

  const handleGenerateNew = () => {
    setOverview(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 bg-gray-50 overflow-y-auto flex items-center justify-center">
        <div className="container">
          <div className="flex items-center gap-2 text-gray-500 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading overview...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Header - Fixed, outside scrollable area */}
      <div className="shrink-0 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between gap-4 px-8 py-4">
          {/* Title */}
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900 truncate">
              Project Overview
            </h1>
            <p className="text-sm text-gray-500 truncate">
              Overview for {projectName}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Edit/Save/Cancel buttons when overview exists */}
            {overview && (
              isEditing ? (
                <>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-colors text-gray-700"
                  >
                    <X className="w-4 h-4" />
                    <span className="text-base">Cancel</span>
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSaving || !hasEditChanges}
                    className={cn(
                      "h-[42px] px-4 flex items-center gap-2 rounded-[10px] transition-colors",
                      hasEditChanges
                        ? "text-white bg-brand-primary hover:bg-brand-primary/90"
                        : "text-gray-400 bg-gray-200 cursor-not-allowed"
                    )}
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    <span className="text-base">{isSaving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Hide Edit and Generate buttons when viewing old version */}
                  {!viewingVersion && (
                    <>
                      <button
                        onClick={handleStartEdit}
                        className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <Pencil className="w-4 h-4" />
                        <span className="text-base">Edit</span>
                      </button>
                      <button
                        onClick={handleGenerateNew}
                        className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <Sparkles className="w-4 h-4" />
                        <span className="text-base">Generate New</span>
                      </button>
                    </>
                  )}
                  
                  {/* Versions Dropdown */}
                  {versions.length > 1 && (
                    <VersionsDropdown
                      versions={versions}
                      currentVersionId={viewingVersion ? viewingVersion.id : 'current'}
                      onVersionSelect={handleVersionSelect}
                    />
                  )}
                </>
              )
            )}

            {/* Ask AI Button */}
            <button
              onClick={() => setShowAskAI(!showAskAI)}
              className={cn(
                "h-10 px-4 flex items-center gap-2 rounded-[10px] transition-colors text-white whitespace-nowrap",
                showAskAI 
                  ? "bg-brand-primary/80" 
                  : "bg-brand-primary hover:bg-brand-primary/90"
              )}
            >
              <Sparkles className="w-4 h-4 shrink-0" />
              <span className="text-base">Ask AI</span>
            </button>

            {/* Copy Button */}
            <button 
              onClick={handleCopy}
              className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-[10px] hover:bg-gray-50 transition-colors text-gray-700 whitespace-nowrap"
            >
              <Copy className="w-4 h-4 shrink-0" />
              <span className="text-base">Copy</span>
            </button>
          </div>
        </div>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 bg-gray-50 overflow-y-auto">
        <div className="container">
        {/* Ask AI Bar */}
        {showAskAI && (
          <div className="px-8 pt-6">
            <AskAIBar
              isOpen={showAskAI}
              onClose={() => setShowAskAI(false)}
              onSend={handleAskAI}
              onInsertResponse={handleInsertAIResponse}
              isLoading={chatMutation.isPending}
            />
          </div>
        )}

        {/* Content Area */}
        {overview ? (
          /* Display saved/generated overview */
          <div className="px-8 py-8">
            {/* Viewing Old Version Banner */}
            {viewingVersion && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-amber-700 font-medium">
                      Viewing {viewingVersion.name}
                    </span>
                    {viewingVersion.timestamp && (
                      <span className="text-amber-600 text-sm">
                        ({viewingVersion.timestamp})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleCancelViewingVersion}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      Back to Current
                    </button>
                    <button
                      onClick={handleRestoreVersion}
                      disabled={isRestoringVersion}
                      className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors disabled:opacity-50"
                    >
                      {isRestoringVersion ? 'Restoring...' : 'Restore This Version'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className={cn(
              "bg-white rounded-[14px] shadow-sm transition-all",
              isEditing
                ? "border-2 border-purple-400 shadow-[0_0_0_4px_rgba(147,51,234,0.1)]"
                : "border border-gray-200"
            )}>
              {/* Content */}
              <div className={isEditing ? "" : "p-6"}>
                <ProjectOverviewContent
                  content={isEditing ? editContent : formattedContent}
                  isEditing={isEditing}
                  onContentChange={handleInlineContentChange}
                  editorKey={editorKey}
                />
              </div>

              {/* Footer with metadata */}
              {!isEditing && (
                <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50 rounded-b-[14px]">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {viewingVersion ? (
                        <>Viewing {viewingVersion.name}</>
                      ) : (
                        <>
                          Version {overview.version}
                          {overview.createdBy?.name && ` • Created by ${overview.createdBy.name}`}
                          {overview.isDraft && ' • Draft'}
                        </>
                      )}
                    </span>
                    <span>
                      Last updated: {new Date(overview.updatedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Empty State Generator */
          <div className="px-8 py-8">
            <div className="max-w-[585px] mx-auto">
              {/* Icon and Title */}
              <div className="text-center mb-8">
                {/* Document Icon */}
                <div className="w-20 h-20 mx-auto mb-6 bg-[#e0e7ff] rounded-[14px] flex items-center justify-center">
                  <FileText className="w-10 h-10 text-brand-primary" />
                </div>

                <h2 className="text-lg font-medium text-gray-900 mb-3">
                  Create Project Overview
                </h2>

                <p className="text-base text-gray-600 max-w-[448px] mx-auto leading-6">
                  Generate a comprehensive project overview by describing your project goals, uploading project briefs, or providing stakeholder requirements.
                </p>
              </div>

              {/* Input Area with File Attachment */}
              <ArtifactGeneratorInput
                placeholder="Describe your project... (e.g., 'Design workspace for enterprise teams with AI-powered research analysis')"
                onGenerate={handleGenerate}
                isGenerating={isGenerating}
              />

              {/* Footer Text */}
              <p className="text-center text-base text-gray-500 mt-6">
                AI will analyze your input and generate comprehensive content
              </p>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
}
